// Dark-first pre-footer decision-support module.
// Yellow section by design. Never repeats the dark Footer's closer buttons
// (Book / Send Message / Visit Shop). Instead it EDUCATES and ROUTES.
//
// Text on #FFC600 uses BLACK (#000000) for WCAG AAA readability, consistent
// with every other yellow surface on the site. If white-on-yellow is ever
// desired sitewide, flip the two color tokens here in one place.
import React from "react";
import { Link } from "react-router-dom";
import { SITE } from "@/data/config";

const BOOKING = "https://calendly.com/kostaja-info/30min";

// ---------------------------------------------------------------------------
// Copy dictionary. One entry per page context ("variant").
// Every entry: eyebrow, headline, copy, and rows (label + body).
// Rows are RATIONALE, not CTAs. Real CTAs live in `buttons` at the bottom
// and are always navigational, never closing.
// ---------------------------------------------------------------------------
const VARIANTS = {
  default: {
    eyebrow: "How to choose",
    headline: (
      <>
        Start with the problem.
        <br />
        Then choose the right level of help.
      </>
    ),
    copy: "Not every business needs a full project first. Some need an audit. Some need a fixed-scope service. Some need a larger brand, website, or marketing system. The right starting point depends on what is unclear, what is already working, and what needs to happen next.",
    rows: [
      { k: "Audit", v: "Best when something feels wrong but the next move is unclear." },
      { k: "Fixed-scope service", v: "Best when the deliverable is specific and the need is already obvious." },
      { k: "Project", v: "Best when the issue crosses brand, website, content, and systems." },
      { k: "Resource", v: "Best when you want a guide, checklist, workbook, or template you can use yourself." },
    ],
  },
  service: {
    eyebrow: "After you choose",
    headline: "What happens after you choose this service.",
    copy: "No mystery process. Every purchase, quote, or booking follows the same four short steps so the next move is always clear.",
    rows: [
      { k: "You start the request", v: "The button above sends your details, or opens Stripe or Calendly, depending on the service." },
      { k: "Kostaja reviews the context", v: "We read what you sent, look at your current brand, website, or listings, and confirm the fit before any work begins." },
      { k: "You provide a short input", v: "A brief intake or a 30 minute call names the outcome, the audience, and the constraints. No forms for the sake of forms." },
      { k: "Scope is confirmed in writing", v: "A short scope note names the deliverable, timeline, price, and what is not included. Work only starts after you say yes." },
    ],
  },
  industry: {
    eyebrow: "Starting path",
    headline: "A practical starting path for this industry.",
    copy: "The right first move depends on which part of the business is loudest. Read the four and pick the one that matches your situation.",
    rows: [
      { k: "Start with review or audit", v: "If the issue is unclear and you are not sure where the loss is happening." },
      { k: "Start with brand", v: "If trust and consistency are the weak points across touchpoints." },
      { k: "Start with website", v: "If the offer is hard to explain or the site is not converting attention to conversations." },
      { k: "Start with local visibility", v: "If discovery is the issue and nearby customers are not finding you." },
    ],
  },
  need: {
    eyebrow: "Fix first",
    headline: "Fix the right thing first.",
    copy: "Most weak outcomes come from solving the wrong problem first. Read the diagnosis rows and pick the level of help that fits.",
    rows: [
      { k: "Diagnose the issue first", v: "If two symptoms are true at once, an audit or a 30 minute call names the sequence before you spend on the wrong fix." },
      { k: "Use a fixed-scope service", v: "If the deliverable is already obvious (a logo system, a page rewrite, a listing kit) and the outcome is well known." },
      { k: "Use a project", v: "If the issue crosses brand, website, and content, and needs coordinated work shaped by commercial judgment." },
      { k: "Book a strategy call", v: "If the starting point is genuinely unclear. Thirty minutes usually names the strongest first move." },
    ],
  },
  portfolio: {
    eyebrow: "Apply this",
    headline: "See how this could apply to your business.",
    copy: "Every project on this page maps to real services with fixed outcomes. Read the three rows and follow the one that matches the change you want to see in your own business.",
    rows: [
      { k: "What this project improved", v: "Trust at first impression, clarity of the offer, or discoverability, depending on the piece." },
      { k: "Services that create similar outcomes", v: "The Services page lists every fixed-scope option that ladders into work like this." },
      { k: "Where to start if the fit is close", v: "Browse by Need if the problem is clear. Book if you want a working session to name the first move." },
    ],
  },
  blog: {
    eyebrow: "Turn idea into action",
    headline: "Turn this idea into a practical next step.",
    copy: "The note is only useful if it changes what you do next. Use one of the three routes below to move from reading to acting.",
    rows: [
      { k: "If the idea named a specific gap", v: "Open the matching service. Every service page shows the outcome, the deliverable, and the price." },
      { k: "If the idea named a general problem", v: "Browse by Need. Each need shows a recommended sequence, not a menu." },
      { k: "If you want to talk it through", v: "Book a working call. No pitch, no discovery template. Kostaja names the honest starting point." },
    ],
  },
  podcast: {
    eyebrow: "Use the idea",
    headline: "Use the idea before you move on.",
    copy: "The episode is only worth the time if it changes a decision. Pick the route that matches what the conversation surfaced for you.",
    rows: [
      { k: "Related services", v: "If the episode named a specific gap, the matching service is the shortest path to closing it." },
      { k: "Related notes", v: "If you want to read further before acting, open the Notes for a longer written treatment." },
      { k: "A working call", v: "If it is easier to talk it through, book a 30 minute session with studio leadership." },
    ],
  },
  contact: {
    eyebrow: "What to expect",
    headline: "What happens after you reach out.",
    copy: "Every message goes directly to studio leadership. No sales script. No auto-responder trap. Here is what your inbox will see, and when.",
    rows: [
      { k: "Confirmation within one working day", v: "A short reply naming what we heard and any clarifying questions." },
      { k: "A recommended next step in plain English", v: "Either a fixed-scope service, an audit, a project quote, or a working call, depending on what you described." },
      { k: "A scope note before any work begins", v: "For projects and audits, a written scope names the deliverable, the timeline, the price, and what is not included. Work only starts after you say yes." },
      { k: "No pressure to buy", v: "If Kostaja is not the fit, we will say so and point you at a stronger option." },
    ],
  },
};

// ---------------------------------------------------------------------------
// Buttons. Yellow section buttons are always navigational, never a hard close.
// Footer already covers Book / Send Message.
// ---------------------------------------------------------------------------
const BUTTON_SETS = {
  default: [
    { label: "Browse by Need", to: "/needs", testId: "cta-browse-needs" },
    { label: "Explore Services", to: "/services", testId: "cta-explore-services" },
    { label: "Book if Unsure", href: BOOKING, external: true, testId: "cta-book-if-unsure" },
  ],
  // On service detail, the primary CTA already lives in the sidebar PriceCard.
  // Yellow section supplies rationale and lateral routing only.
  service: [
    { label: "Browse by Need", to: "/needs", testId: "cta-browse-needs" },
    { label: "Explore Services", to: "/services", testId: "cta-explore-services" },
  ],
  industry: [
    { label: "Browse by Need", to: "/needs", testId: "cta-browse-needs" },
    { label: "Explore Services", to: "/services", testId: "cta-explore-services" },
    { label: "Book if Unsure", href: BOOKING, external: true, testId: "cta-book-if-unsure" },
  ],
  need: [
    { label: "Explore Services", to: "/services", testId: "cta-explore-services" },
    { label: "Book if Unsure", href: BOOKING, external: true, testId: "cta-book-if-unsure" },
  ],
  portfolio: [
    { label: "Browse by Need", to: "/needs", testId: "cta-browse-needs" },
    { label: "Explore Services", to: "/services", testId: "cta-explore-services" },
  ],
  blog: [
    { label: "Browse by Need", to: "/needs", testId: "cta-browse-needs" },
    { label: "Explore Services", to: "/services", testId: "cta-explore-services" },
    { label: "Book if Unsure", href: BOOKING, external: true, testId: "cta-book-if-unsure" },
  ],
  podcast: [
    { label: "Explore Services", to: "/services", testId: "cta-explore-services" },
    { label: "Read the Notes", to: "/blog", testId: "cta-read-notes" },
    { label: "Book if Unsure", href: BOOKING, external: true, testId: "cta-book-if-unsure" },
  ],
  // Contact page: reassurance, no CTA stack. The form itself is the action.
  contact: [],
};

const Button = ({ btn }) => {
  const cls =
    "inline-flex items-center justify-between font-semibold px-6 py-4 border-2 border-black text-black hover:bg-black hover:text-[#FFC600] transition-colors";
  if (btn.external) {
    return (
      <a href={btn.href} target="_blank" rel="noreferrer" data-testid={btn.testId} className={cls}>
        <span>{btn.label}</span>
        <span aria-hidden>↗</span>
      </a>
    );
  }
  return (
    <Link to={btn.to} data-testid={btn.testId} className={cls}>
      <span>{btn.label}</span>
      <span aria-hidden>→</span>
    </Link>
  );
};

const CTABlock = ({ variant = "default" }) => {
  const v = VARIANTS[variant] || VARIANTS.default;
  const buttons = BUTTON_SETS[variant] || BUTTON_SETS.default;

  return (
    <section
      data-testid="cta-block"
      data-variant={variant}
      className="bg-[#FFC600] text-black"
    >
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="k-overline text-black/70 mb-4">{v.eyebrow}</div>
            <h2 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-[0.92]">
              {v.headline}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-8">
            <p className="text-base lg:text-lg text-black/80 max-w-xl">{v.copy}</p>
          </div>
        </div>

        <ul
          data-testid="cta-decision-rows"
          className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/15 border-2 border-black"
        >
          {v.rows.map((r) => (
            <li
              key={r.k}
              data-testid={`cta-row-${r.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-[#FFC600] p-6 lg:p-7"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black">
                {r.k}
              </div>
              <p className="mt-2 text-black/85 text-sm lg:text-base leading-snug">{r.v}</p>
            </li>
          ))}
        </ul>

        {buttons.length ? (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {buttons.map((b) => (
              <Button key={b.testId} btn={b} />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default CTABlock;
